import { HttpClient } from '@angular/common/http';
import { Component, HostListener, inject, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { faUnlock, faInfoCircle, faUser, faBuildingColumns, faPowerOff } from '@fortawesome/pro-solid-svg-icons';

import { SmartyStreetsService } from '../../core/common/smart-streets.service';
import { SecurityService } from '../../core/security/security.service';
import { SystemMessage, SystemMessageService } from '../../core/system-message/system-message-service';
import { EditUserVm } from '../../shared/generated/Administration/Models/User/EditUser/EditUserVm';
import { SelectItem } from '../../shared/models/select-item';
import { UserPermissionsModalComponent } from './user-permissions-modal/user-permissions-modal.component';
import { PcgSelectComponent } from '../../shared/form-elements/components/pcg-select-component/select.component';
import { ActivationService } from '../../shared/service/activation.service';
import { ActivationEnum } from '../../core/enums/generated/ActivationEnum';
import { UserJsVm } from '../../shared/generated/Administration/Models/User/JavaScript/UserJsVm';
import { PermissionProfile } from '../../core/enums/generated/PermissionProfile';
import { GlobalVariablesService } from '../../services/global-variables.service';
import { LoginTypeEnum } from '../../core/enums/generated/LoginTypeEnum';
import { NavigationService } from '../../shared/navigation/navigation.service';
import { GlobalService } from '../../shared/service/global.service';

@Component({
    selector: 'pcg-edit-user',
    templateUrl: './edit-user.component.html',
    styleUrls: ['./edit-user.component.scss'],
    standalone: false
})
export class EditUserComponent implements OnInit {
	
	@ViewChild('orgs', { static: false }) selectOrgs: PcgSelectComponent;

    navService = inject(NavigationService);

	userNameValidation = { pattern: "Only letters, numbers, and symbols ( . @ - _ ' ) are allowed" };
	fullNameValidation = { pattern: "Only letters and symbols - ' . are allowed" };
	phoneValidation = { pattern: "Invalid phone number"	};		
	
	formGroup = EditUserVm.Form;
	model = EditUserVm.GetModel(this.formGroup);
	user: UserJsVm;

	// Security variables
	isLockedOut = false;
	canManageUsers = false;
	canSeePasswordButtons = false;
	isAdmin = false;
	isCurrentUser = false;
	showInactive = false;
	isSysAdmin = false;
	iAmMe: boolean;
	showNewUserMessage = false;
	firstLoad = false;
	newUserParam = false;
	isPcg = false;
	isSSO = false;
    isMobile = false;

	id: number;

	displayMessage: 'block' | 'none' = 'none';
	
	timeout: any
	
	// Fa icons
	faIconName = { faUnlock, faInfoCircle, faUser, faBuildingColumns, faPowerOff };	

	constructor(
		private route: ActivatedRoute
		, private httpClient: HttpClient
		, public router: Router
		, private modalService: NgbModal
		, private ms: SystemMessageService
		, public smartyStreetsService: SmartyStreetsService
		, private sec: SecurityService
		, private activationService: ActivationService
		, private gvs: GlobalVariablesService
	) { }

    @HostListener('window:resize')
	onResize() { this.isMobile = GlobalService.setIsMobile(window.innerWidth); }

	ngOnInit() {
        this.isMobile = GlobalService.setIsMobile(window.innerWidth);
		this.user = this.sec.getUser();
		this.isSysAdmin = this.sec.getSecurity()?.permissionProfile === PermissionProfile.SystemAdmin;
		this.addSecuritySubscription();
		this.addRouteSubscription();
	}

	/** Used to open audit history */
	openWindow = (o: string) => { this.router.navigateByUrl('/' + o); };

	populateOrgs() {
		this.httpClient.get('api/Select/Organizations/?showInactive=' + this.showInactive)
			.subscribe((items: SelectItem[] | any) => { this.selectOrgs.items = items; });
	}

	fullNameChange() {
		if (this.model.isNewUser) {
			var uName = this.formGroup.controls.fullName.value;
			if (uName.indexOf(" ") > -1) {
				var nameSplit = this.formGroup.controls.fullName.value.split(" ");
				var newStr = Array.from(nameSplit[0])[0] + nameSplit[1];
				this.formGroup.controls.userName.patchValue(newStr);
			} else { this.formGroup.controls.userName.patchValue(uName); }
		}
	}

	/** Update the local dynamic security object when the security updates */
	addSecuritySubscription() {
		this.sec.security$.subscribe(() => {
			this.isAdmin = this.sec.hasAccess(o => o.adminFeatures);
			this.canManageUsers = this.sec.hasAccess(o => o.manageSiteUsers);	
		});
	}

	/** Subscribe to route to pick up id changes and update page accordingly */
	addRouteSubscription() {
		this.route.paramMap.subscribe(map => {
			// Allow user to view page if it is either their profile
			// or they have admin access to view other people's profiles
			const user = this.sec.getUser();
			this.id = parseInt(map.get('id')!, 10);
			let api = this.newUserParam === true 
				? `api/Administration/Users/GetUser/${this.id}`
				: `api/Administration/Users/GetUser/${this.id}?isNewUser=${true}`;
			if (
				!this.canManageUsers 
				&& this.id !== user.id
			) { this.router.navigateByUrl('dashboard'); }
			this.isCurrentUser = this.id === this.sec.getUser().id;
            this.iAmMe = user?.id === this.id;
			this.httpClient.get(api)
				.subscribe((vm: EditUserVm | any) => {
					if (vm?.email?.includes("@paulconsulting.com")) { this.isPcg = true; }
					else { this.isPcg = false; }
					if (!this.isAdmin) { this.formGroup.controls.organizationId.disable(); }

					this.formGroup.patchValue(vm);
					if (vm?.isActive === false) {
						this.showInactive = true;
						this.populateOrgs();
					}
					if (
						(
							vm.orgLoginType === LoginTypeEnum.SSO
							|| vm.orgLoginType == LoginTypeEnum.Both
						)
						&& !this.isSysAdmin
					) { 
						this.isSSO = true; 
						this.formGroup.controls.email.disable();
					} else { 
						this.isSSO = false; 
						this.formGroup.controls.email?.enable();
					}

					this.canSeePasswordButtons = vm.isActive 
						&& !vm.isLockedOut 
						&& vm.userId !== this.sec.getUser().id 
						&& vm.userId !== 0;
					this.isLockedOut = vm.isLockedOut;
					
					if (vm.userId != 0) { this.gvs.userName.set(vm.fullName); }
					else { this.gvs.userName.set("Create User"); }
					this.sec.setControlAccess(this.formGroup?.controls?.permissionId, o => o.manageSiteUsers);
				}
			);
		});
	}

	changeOrg() {
		if (
			this.selectOrgs?.value === null
			|| this.selectOrgs?.value === undefined
			|| this.selectOrgs?.value === 0
		) { this.isSSO = false; }
		else {
			if (
				(
					this.selectOrgs?.additionalData?.loginType === LoginTypeEnum.SSO
					|| this.selectOrgs?.additionalData?.loginType === LoginTypeEnum.Both
				)
				&& !this.isSysAdmin
			) { 
				this.isSSO = true; 
				this.formGroup.controls.email.disable();
			} else { 
				this.isSSO = false; 
				this.formGroup.controls.email?.enable();
			}
		}
	}

	/** The main save event for the page */
	onMessageSuccess(systemMessage: SystemMessage) {
		const isCreatingUser = this.id === 0;
		this.formGroup.setValue(systemMessage.model);
		const model = this.formGroup.value;
		
		if (isCreatingUser) {
			this.router.navigateByUrl(`/users/edit/associate-sites/${model.userId}?isCreatingNewUser=true`);
		} else if (this.canManageUsers) {		
			this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => { this.router.navigate([`/users/edit/details/${model.userId}`]); });
		}
		this.canSeePasswordButtons = model.isActive 
			&& !model.isLockedOut 
			&& model.userId !== this.sec.getUser().id 
			&& model.userId !== 0;
		this.sec.getAndSetSecurity();
	}

	/** Unlock user, called when the unlock svg clicked on locked user */
	unlockUser() { this.ms.getHttpObservable(this, `api/Administration/Users/UnlockUser/${this.id}`).subscribe(() => { this.isLockedOut = false; }); }

	activateDeactivate() { 
		this.activationService.activateDeactivate(
			this.id
			, ActivationEnum.User
			, this.model.isActive ? false : true
		); 
	}

	/** Called on click of the reset password and welcome email buttons */
	resetPassword(
        isWelcomeEmail: boolean
        , isSystemMessage: boolean
    ) {
		this.httpClient.post(
            'api/Account/ForgotPassword'
            , {
			    email: this.formGroup.value.email
			    , isWelcomeEmail
		    }
        ).subscribe((msg: SystemMessage | any) => {
			if (msg.isSuccessful) {
				this.ms.setSystemMessage(`An email has been sent to the user with a ${isWelcomeEmail ? 'welcome' : 'reset password'} link.`);
			} else { this.ms.setSystemMessage('An error occurred when attempting to send the email.', 'error'); }
		});
		// timed out so that the user can see the confirmation system message before being redirected
		if (isSystemMessage) { setTimeout(() => { this.router.navigateByUrl("users/list") }, 500); }	
	}

	openPermissionsInfoModal() { this.modalService.open(UserPermissionsModalComponent, { size: 'xl' }); }

    // #region Logout
    executeLogout() {
		this.gvs.isNavFixed.set(false);
        this.navService.collapseAllSubNavs();
        this.sec.setSecurity(null!, null!, null!);
        localStorage.removeItem("subNavPosition");
        sessionStorage.clear();
        this.deleteAllCookies();

        var loggedInUsingSso = localStorage.getItem("loggedInUsingSso") == "true";
        localStorage.setItem("loggedInUsingSso", "false");
        if (loggedInUsingSso) {
            this.httpClient.get(
                `api/Account/SingleSignOut` 
                + `?userId=${this.user.id}` 
                + `&entraIdentifierUrl=${localStorage.getItem("entraIdentifierUrl")}` 
                + `&entraLoginUrl=${localStorage.getItem("entraLoginUrl")}`
            ).subscribe((msg: any) => {
                localStorage.setItem("entraIdentifierUrl", "");
                localStorage.setItem("entraLoginUrl", "");
                if (msg.isSuccessful) { window.location.href = msg.value; }
                else { this.ms.setSystemMessage(msg.message, msg.messageClass); }
            });
        }
        this.navService.setOpenSecondaryMenu([]);
        this.router.navigate(['/login']);
	}

	deleteAllCookies() {
        const cookies = document?.cookie?.split(";");

        for (let i = 0; i < cookies.length; i++) {
            const cookie = cookies[i];
            const eqPos = cookie.indexOf("=");
            const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
            document.cookie = name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT";
        }
    }
	//#endregion  
}